import LoginButton from "@/components/ui/LoginButton";
import UserMenu from "@/components/ui/UserMenu";
import { Outlet, NavLink, useParams } from "react-router-dom";
import { CalendarDays, Home, Menu, Trophy } from "lucide-react";
import { lazy, Suspense, useContext } from "react";
import { useAuth } from "@/hooks/auth/useAuth";
import { useTour } from "@/hooks/tours/useTour";
import { useTourMembership } from "@/hooks/tours/useTourMembership";
import {
  createTourContextValue,
  TourContext
} from "@/app/tours/TourContext";
import { FLAGSHIP_TOUR_ID } from "@/app/routing/tourRoutes";

const AchievementInboxGate = lazy(
  () => import("@/player/components/AchievementInboxGate")
);

export default function PublicLayout() {
  const { tourId: routeTourId } = useParams<{ tourId?: string }>();
  const { firebaseUser } = useAuth();
  const resolvedTourId = routeTourId ?? FLAGSHIP_TOUR_ID;
  const { tour, loading, exists } = useTour(resolvedTourId);
  const { membership } = useTourMembership(resolvedTourId, firebaseUser?.uid);

  if (loading) {
    return <div className="p-6 text-mutedText">Loading tour...</div>;
  }

  if (!exists || !tour) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas p-6 text-center">
        <div>
          <div className="text-2xl font-bold text-text">Tour not found</div>
          <div className="mt-2 text-sm text-mutedText">
            This tour is unavailable or requires an invitation.
          </div>
        </div>
      </div>
    );
  }

  const context = createTourContextValue(
    tour,
    membership ?? undefined,
    tour.id === FLAGSHIP_TOUR_ID
  );

  return (
    <TourContext.Provider value={context}>
      <PublicLayoutContent />
    </TourContext.Provider>
  );
}

function PublicLayoutContent() {
  const { tour, path } = useContext(TourContext)!;

  return (
    <div className="min-h-[100dvh] bg-background text-text flex flex-col">
      {/* ===== Desktop Header ===== */}
      <header className="hidden md:flex items-center justify-between px-10 py-6 border-b border-muted/20 bg-white">
        <div className="text-xl font-semibold tracking-wide">{tour.name}</div>

        <nav className="flex gap-8 text-sm font-medium">
          <NavItem to={path()} label="Home" />
          <NavItem to={path("/schedule")} label="Schedule" />
          <NavItem to={path("/standings")} label="Standings" />
          <NavItem to={path("/more")} label="More" />
        </nav>

        <div className="flex items-center gap-4">
          <LoginButton />
          <UserMenu />
        </div>
      </header>

      {/* ===== Main Content ===== */}
      <main className="flex-1 px-6 md:px-10 py-10 pb-28 md:pb-10">
        <Outlet />
      </main>

      {/* ===== Mobile Bottom Nav ===== */}
      <nav className="fixed inset-x-0 bottom-0 z-50 border-t border-black/5 bg-surface/95 px-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] pt-2 shadow-[0_-14px_32px_rgba(0,0,0,0.08)] backdrop-blur md:hidden">
        <div className="mx-auto grid max-w-md grid-cols-4 gap-1">
          <MobileNavItem to={path()} label="Home" icon={<Home size={19} />} />
          <MobileNavItem
            to={path("/schedule")}
            label="Schedule"
            icon={<CalendarDays size={19} />}
          />
          <MobileNavItem
            to={path("/standings")}
            label="Standings"
            icon={<Trophy size={19} />}
          />
          <MobileNavItem to={path("/more")} label="More" icon={<Menu size={19} />} />
        </div>
      </nav>

      <Suspense fallback={null}>
        <AchievementInboxGate />
      </Suspense>
    </div>
  );
}

function NavItem({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `transition-colors ${
          isActive ? "text-primary" : "text-muted hover:text-text"
        }`
      }
    >
      {label}
    </NavLink>
  );
}

function MobileNavItem({
  to,
  label,
  icon
}: {
  to: string;
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex h-14 min-w-0 flex-col items-center justify-center gap-1 rounded-2xl px-2 text-[11px] font-semibold transition ${
          isActive
            ? "bg-primary text-white shadow-sm"
            : "text-muted hover:bg-black/5 hover:text-text"
        }`
      }
    >
      <span className="leading-none">{icon}</span>
      {label}
    </NavLink>
  );
}

